import {
  DATA_SCOPE,
  USER_TYPE_META,
  labelOf,
  normalizeEnumOrNull,
  type DataScope,
  type RegionCode,
} from '../enums';
import type { CurrentUser, ProfileSnapshot } from '../types/models';
import { asUserType, normalizeEnds } from './jwt';

/**
 * §6.2-7 `GET /auth/profile` 快照 → 前端 `CurrentUser`（权限态唯一权威重建路径）。
 *
 * 为什么要这一层：后端把权限拆成 `menuPermissions`（侧栏渲染）与 `buttonPermissions`
 * （`v-perm` 按钮级）两份清单，并有 `role` 内嵌对象；而前端 RBAC 只认一个扁平
 * `permissions: string[]`（见 `core/rbac/permission.ts`）。这里做**结构归一**，
 * 视图与指令零改动。
 *
 * 两条不可让的口径：
 * 1. **dataScope 未知值按最小权限 `SELF_ONLY` 落位，绝不回落 `ALL`** —— 未知码更可能是
 *    新增受限范围，猜成 ALL 等于自行放大可见数据面（虽然后端才是行级过滤的边界）。
 * 2. 区域码是服务端业务主数据，不是权限枚举；仅接受非空字符串，trim 后原码保留。
 */
export function userFromProfileSnapshot(snap: ProfileSnapshot): CurrentUser {
  const userType = asUserType(snap.userType);
  const permissions = Array.from(new Set([...(snap.menuPermissions ?? []), ...(snap.buttonPermissions ?? [])]));
  const endBinding = normalizeEnds(snap.endBinding);
  const regionCodes: RegionCode[] = [];
  (snap.regionCodes ?? []).forEach((code) => {
    if (typeof code !== 'string') return;
    const value = code.trim();
    if (value && !regionCodes.includes(value)) regionCodes.push(value);
  });
  const dataScope = normalizeEnumOrNull(snap.dataScope ?? snap.role?.dataScope, Object.values(DATA_SCOPE) as DataScope[]) ?? DATA_SCOPE.SELF_ONLY;

  return {
    id: String(snap.userId ?? snap.account),
    account: snap.account,
    name: snap.name,
    userType,
    userTypeLabel: labelOf(USER_TYPE_META, userType),
    roleCode: snap.role?.roleCode ?? '',
    roleName: snap.role?.roleName ?? '',
    regionCodes,
    endBinding,
    dataScope,
    permissions,
    permVersion: Number(snap.role?.permVersion ?? 0),
    avatarText: (snap.name || snap.account || '').slice(0, 1),
    // v1.4 路甲：机构归属只认服务端回显的键，没回就是 undefined，⛔ 由前端按角色猜一个
    agencyId: snap.agencyId === undefined ? undefined : String(snap.agencyId),
    agencyName: snap.agencyName || undefined,
  };
}
